import React, { useState, useEffect } from "react";
import { usePermissionContext } from "../../contexts/permission-context";
import Filter from "../../components/Filter/Filter";
import {
    fetchAPIData,
    getCompanyId,
    getToken,
    getUser,
    copyToClipboard,
    generateHiddenColumns,
    hasPermission,
    assetsPermissionOptions,
    assetsUseManageOptions,
    createFilterOptions, createSortFieldOptions
} from '../../Utils/Common';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import RotateRightOutlinedIcon from '@mui/icons-material/RotateRightOutlined';
import CopyIcon from '@mui/icons-material/AddToPhotosOutlined';
import InfoIcon from '@mui/icons-material/Info';
import IconButton from '@mui/material/IconButton';
import Table, { renderCellExpand } from '../../components/Table/Table';
import { GridActionsCellItem } from "@mui/x-data-grid";
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import Link from '@mui/material/Link';
import ActionDialog from "../../components/Dialogs/ActionDialog";
import DraggableDialog from "../../components/Dialogs/DraggableDialog";
import { FormDialog } from "../../components/FormDialog/FormDialog";
import axios from 'axios';
import "../pages.scss";
import { usePersistentStorage } from "../../Utils/usePersistentStorage";
import { Typography } from "@mui/material";
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import DocumentIDPWorkflowStatusDialog from "./DocumentIDPWorkflowStatusDialog";

const documentProcessStatusSchemaView = require("../../json/documentProcessStatusSchema.json").view;
//const idpWorkflowCspDocumentStatusSchemaView = require("../../json/idpWorkflowDocumentStatusSchema.json").view;

const uiSchema = {
    "allDocumentGUIDs": {
        "ui:options": {
            orderable: false,
            addable: false,
            removable: false
        }
    }
};

const DocumentProcessStatusTabPanel = ({ setNotify }) => {
    const { permissions } = usePermissionContext();
    const [apiData, setApiData] = useState([]);
    const [rowJsonData, setRowJsonData] = useState({});
    const [idpWorkflowCspDocumentStatusJsonData, setIdpWorkflowCspDocumentStatusJsonData] = useState([]);
    const [open, setOpen] = useState(false);
    const [openIdpWorkflowCspDocumentStatusDialog, setOpenIdpWorkflowCspDocumentStatusDialog] = useState(false);
    const [mode, setMode] = useState('view');  // default to view mode
    //const [idpWorkflowCspDocumentStatusMode, setIdpWorkflowCspDocumentStatusMode] = useState('view');  // default to view mode
    const [openInfoDialog, setOpenInfoDialog] = useState(false);

    const [reload, setReload] = useState(false);
    const [isLoading, setIsloading] = useState(false);

    const [processStatus, setProcessStatus] = usePersistentStorage("processStatus", []);

    const origination = "CSP";
    const viewTitle = "IDP Workflow CSP Document Status Information";
    const jsonDataName = "IDP workflow CSP document status";

    
    const hasAssetsUsePermission = hasPermission(permissions, assetsUseManageOptions);

    useEffect(() => {
        setApiData(processStatus);
    }, []);

    const reprocessDialogTitle = "Reprocess Instruction";

    const reprocessInstructions =
        <>
            <Typography variant="body1" paragraph="true">
                <RotateRightOutlinedIcon style={{ verticalAlign: "middle" }} /> Conditional Reprocess: Server only updates the Machine Learning (ML) results to the CSP tenant.
            </Typography>

            <Typography variant="body1" paragraph="true">
                <AutorenewIcon style={{ verticalAlign: "middle" }} /> Force Reprocess: Server sends the docs to the ML and updates the ML results to the CSP tenant when available.Partial or Full.
            </Typography>

            <Typography variant="body1" paragraph="true">
                The two buttons below the filter reprocess everything on the page.
                The bulk reprocess actions will skip any documents in DOCUMENT_DELETED or WORKFLOW_DELETED state.
            </Typography>

            <Typography variant="body1" paragraph="true">
                The two reprocess icons in the table reprocess individual document.
                The reprocess icons will be disabled for documents in DOCUMENT_DELETED or WORKFLOW_DELETED state.
            </Typography>
        </>;

    const idpCspWorkflowInstructions = "Click the 'Process State' cell link in order to view IDP CSP workflow status";

    // API URL is dynamic in that COMPANY_ID is replaced
    const apiURL = '/api/companies/%COMPANY_ID%/documents/process/states?';

    const totalLabel = "Matching Documents";

    const fieldData = [
        'liveDocumentGUID', 'allDocumentGUIDs', 'originalDocumentGUID', 'workflowDocumentGUIDs',
        'fileName', 'documentType', 'isCSP', 'processType', 'state', 'mlResultFile',
        'createdDate', 'modifiedDate'
    ];

    const sortFieldData = [
        // {
        //     field: 'companyId',
        //     label: 'Company ID'
        // },
        {
            field: 'liveDocumentGUID',
            label: 'Live Document GUID'
        },
        {
            field: 'originalDocumentGUID',
            label: 'Original Document GUID'
        },
        {
            field: 'fileName',
            label: 'Filename'       // correct??
        },
        {
            field: 'documentType',
            label: 'Document Type'
        },
        {
            field: 'state',
            label: 'State'
        },
        {
            field: 'mlResultFile',
            label: 'ML Result File'
        },
        {
            field: 'createdDate',
            label: 'Created Date'
        },
        {
            field: 'modifiedDate',
            label: 'Modified Date'
        }
    ];


    const columns = [
        // {
        //     field: 'id',
        //     type: 'number',
        //     headerName: 'ID',
        //     hide: true,
        //     minWidth: 50,
        //     flex: 1,
        //     headerAlign: 'left',
        //     align: 'left',
        // },
        {
            field: 'actions',
            type: 'actions',
            hideable:false,
            headerName: 'Actions',
            minWidth: 120,
            maxWidth: 120,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            getActions: (params) => getActionButtons(params),
            renderHeader: (params) => (
                <>
                    {params.colDef.headerName}
                    <IconButton aria-label="reprocess instructions" onClick={showReprocessInstructions}>
                        <InfoIcon />
                    </IconButton>
                </>
            ),
        },
        {
            field: 'companyId',
            hideable:false,
            headerName: 'Company ID',
            type: 'number',
            minWidth: 100,
            maxWidth: 100,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            valueFormatter: (params) => {
                return params.value.toString();
            }
        },
        {
            field: 'processType',
            hideable:false,
            headerName: 'Process Type',
            minWidth: 150,
            maxWidth: 150,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: (params) => {
                return (
                    // <span style={{ color: params.value.localeCompare("DOCUMENT") === 0 ? '#66E7D4' : '#3371FF' }}>{params.value}</span>
                    <>
                        {params.value.localeCompare("DOCUMENT") === 0 &&
                            <Chip
                                className="type1"
                                avatar={<Avatar>D</Avatar>}
                                label={params.value}
                                variant="outlined"
                                size="small"
                            />
                        }
                        {params.value.localeCompare("WORKFLOW") === 0 &&
                            <Chip
                                className="type2"
                                avatar={<Avatar>W</Avatar>}
                                label={params.value}
                                variant="outlined"
                                size="small"
                            />
                        }
                        {params.value.localeCompare("CLASSIFICATION") === 0 &&
                            <Chip
                                className="type3"
                                avatar={<Avatar>C</Avatar>}
                                label={params.value}
                                variant="outlined"
                                size="small"
                            />
                        }
                    </>
                );
            },
        },
        {
            field: 'liveDocumentGUID',
            hideable:false,
            headerName: 'Document GUID',
            minWidth: 325,
            maxWidth: 325,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: (params) => {
                return (
                    <div className="guidContent">
                        <div style={{ width: '275px' }}>
                            {params.value}
                        </div>
                        {params.value &&
                            <div>
                                <Tooltip title="Copy to clipboard" arrow placement="right">
                                    <IconButton aria-label="copy document GUID" onClick={(event) => copyToClipboard(event, params.row.liveDocumentGUID)}>
                                        <CopyIcon sx={{ fontSize: 'small' }} />
                                    </IconButton>
                                </Tooltip>
                            </div>
                        }
                    </div>
                )
            }
        },
        {
            field: 'processStartTime',
            hideable:false,
            headerName: 'Process Start Time',
            minWidth: 275,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            valueFormatter: (params) => {
                let formattedStartTime = "";
                if (params.value > 0) {
                    formattedStartTime = new Date(params.value).toISOString();
                }
                return formattedStartTime;
            },
            valueParser: (value) => {
                const date = new Date(value);
                return date.getTime();
            }
        },
        {
            field: 'originalDocumentGUID',
            headerName: 'Original Document GUID',
            hide: true,
            minWidth: 325,
            maxWidth: 325,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: (params) => {
                return (
                    <div className="guidContent">
                        <div style={{ width: '275px' }}>
                            {params.value}
                        </div>
                        {params.value &&
                            <div>
                                <Tooltip title="Copy to clipboard" arrow placement="right">
                                    <IconButton aria-label="copy original document GUID" onClick={(event) => copyToClipboard(event, params.row.originalDocumentGUID)}>
                                        <CopyIcon sx={{ fontSize: 'small' }} />
                                    </IconButton>
                                </Tooltip>
                            </div>
                        }
                    </div>
                )
            }
        },
        {
            field: 'allDocumentGUIDs',
            headerName: 'All Document GUIDs',
            hide: true,
            minWidth: 325,
            maxWidth: 325,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: renderCellExpand
        },
        {
            field: 'workflowDocumentGUIDs',
            headerName: 'Workflow Document GUIDs',
            hide: true,
            minWidth: 325,
            maxWidth: 325,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: renderCellExpand
        },
        {
            field: 'csp',
            headerName: 'Is CSP',
            type: 'boolean',
            hide: true,
            minWidth: 100,
            maxWidth: 100,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: (params) => {
                return (
                    <>
                        {params.value ? 'Y' : 'N'}
                    </>
                );
            }
        },

        {
            field: 'fileName',
            hideable:false,
            headerName: 'File Name',
            minWidth: 200,
            maxWidth: 200,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: renderCellExpand,
        },
        {
            field: 'documentType',
            hideable:false,
            headerName: 'Document Type',
            minWidth: 250,
            maxWidth: 250,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: renderCellExpand,
        },
        {
            field: 'state',
            headerName: 'Process State',
            hideable:false,
            minWidth: 250,
            maxWidth: 250,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            cellClassName: 'state-header',
            renderHeader: (params) => {
                return (
                    <>
                        <span style={{ fontWeight: 500 }}>{params.colDef.headerName}</span>
                        <Tooltip title={idpCspWorkflowInstructions} arrow placement="top">
                            <InfoIcon style={{ verticalAlign: 'middle', marginLeft: '5px' }} />
                        </Tooltip>
                    </>
                );
            },
            renderCell: (params) => {
                return (
                    <Link
                        component="button"
                        variant="body2"
                        onClick={() => {
                            // get json data
                            fetchIdpWorkflowCspDocumentStatus(params.row.companyId, params.row.liveDocumentGUID, params.row.state);
                        }}
                    >
                        <span style={{ color: params.value.includes("ERROR") ? '#FF0000' : '#000000' }}>{params.value}</span>

                    </Link>
                );
            },
        },
        {
            field: 'workflowData',
            headerName: 'Workflow Data',
            hide: true,
            minWidth: 150,
            maxWidth: 150,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: renderCellExpand,
        },
        {
            field: 'mlResultFile',
            hideable:false,
            headerName: 'ML Result',
            minWidth: 350,
            //maxWidth: 350,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: (params) => {
                return (
                    <>
                        {params.value && (
                            <>
                                {params.value}
                                <Tooltip title="Copy to clipboard" arrow placement="right">
                                    <IconButton aria-label="copy ML Result" onClick={(event) => copyToClipboard(event, params.row.mlResultFile)}>
                                        <CopyIcon sx={{ fontSize: 'small' }} />
                                    </IconButton>
                                </Tooltip>
                            </>

                        )}
                    </>
                );
            }
        },
        {
            field: 'createdDate',
            headerName: 'Created Date',
            hide: true,
            minWidth: 225,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: renderCellExpand,
        },
        {
            field: 'modifiedDate',
            headerName: 'Modified Date',
            hide: true,
            minWidth: 225,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: renderCellExpand,
        },

    ];

    const getColumns = () => {
        return (hasAssetsUsePermission && columns) || columns.filter(obj => { return obj.field !== "actions"});
    };

    const toggleColumnPinning = () => {
        return (hasAssetsUsePermission && 'columnPinning') || '';
    };

    const getActionButtons = (params) => {
        let actionButtons = [];

        let forceReprocessButton = <GridActionsCellItem icon={
            <Tooltip title="Force reprocess: Server sends this document to the ML and updates the ML results to the CSP tenant when available." arrow placement="right">
                <AutorenewIcon />
            </Tooltip>}
            label='Force Reprocess'
            onClick={() => {
                let forceFlag = true;
                reprocessData(params.row.companyId, params.row.liveDocumentGUID, params.row.processType, forceFlag);
            }}
            disabled={params.row.state === 'DOCUMENT_DELETED' || params.row.state === 'WORKFLOW_DELETED'}
        />;

        hasAssetsUsePermission && actionButtons.push(forceReprocessButton);


        let conditionalReprocessButton = <GridActionsCellItem icon={
            <Tooltip title="Conditional Reprocess: Server updates the ML results to the CSP tenant for this document." arrow placement="right">
                <RotateRightOutlinedIcon />
            </Tooltip>}
            label='Conditional Reprocess'
            onClick={() => {
                //conditionalReprocessData(params.row)
                let forceFlag = false;
                reprocessData(params.row.companyId, params.row.liveDocumentGUID, params.row.processType, forceFlag);
            }}
            disabled={params.row.state === 'DOCUMENT_DELETED' || params.row.state === 'WORKFLOW_DELETED'}
        />;

        hasAssetsUsePermission && actionButtons.push(conditionalReprocessButton);

        return actionButtons;

    };

    const reprocessData = (companyId, documentGUID, processType, forceFlag) => {
        //console.log("reprocess button clicked", companyId, documentGUID, processType, forceFlag);

        let reprocessUrl = `/api/companies/${companyId}/documents/${documentGUID}/reprocess?processType=${processType}&force=${forceFlag}`;

        axios({
            method: "put", url: reprocessUrl,
            headers: { "insightUserId": getUser(), "companyId": getCompanyId(), "Authorization": getToken() }
        })
            .then(response => {
                // console.log("Total " + response.data.length + " document(s) were initiated for reprocess (force=" + forceFlag + ")");

                let message = `Reprocess initiated for document [${companyId}, ${documentGUID}]

                Please wait a few minutes and reload the table to view reprocess state`;

                setNotify({
                    isOpen: true,
                    message: message,
                    severity: 'info'
                });

                // reload table data after reprocess
                setReload(true);
            }).catch(error => {
                console.log('error in reprocess documents api call:', error);

                let message = `Failed to initiate reprocess for document [${companyId}, ${documentGUID}]: \n\n ${error.response.statusText} : ${error.response.data.message}`;

                setNotify({
                    isOpen: true,
                    alertTitle: 'Request failed',
                    message: message,
                    severity: 'error'
                });

            });
    };

    const fetchData = async (apiURL2, qparams) => {
        //console.log('inside fetch customer data:', qparams);

        /*
        let message = "Loading customer data ...";
    
        setNotify({
            isOpen: true,
            message: message,
            severity: 'info'
        });
    */
        setIsloading(true);
        await fetchAPIData(apiURL2, qparams)
            .then((data) => {
                //console.log("Updating table data: ", data);
                setApiData(data);
                setProcessStatus(data);
                // clear the reload false now that data is loaded
                setReload(false);
                setIsloading(false);
            })
            .catch((err) => {
                console.log(err);

                let message = `Failed to load document process status data \n\n ${err.response.statusText} : ${err.response.data.message}`;
                setNotify({
                    isOpen: true,
                    alertTitle: 'Request failed',
                    message: message,
                    severity: 'error'
                });

                // clear data since user may have previously loaded data that is already displayed
                setApiData([]);
                setReload(false);
                setIsloading(false);
            });
    };

    const viewRowData = (param, event) => {

        // remove the id from table data as table data only not part of json stored
        const {
            id,
            ...JsonData
        } = param.row;

        setRowJsonData(JsonData);
        setMode('view');
        setOpen(true);
    };

    const formatData = (data) => {
        data.forEach((obj) => {
            obj.startTime = data.startTime > 0 ? new Date(data.startTime).toISOString() : "";
            obj.endTime = data.endTime > 0 ? new Date(data.endTime).toISOString() : "";

            obj.tasks.forEach((obj) => {
                obj.startTime = obj.startTime > 0 ? new Date(obj.startTime).toISOString() : "";

                obj.endTime = obj.endTime > 0 ? new Date(obj.endTime).toISOString() : "";

                obj.scheduledTime = obj.scheduledTime > 0 ? new Date(obj.scheduledTime).toISOString() : "";

                // convert object to JSON string format
                obj.outputData = JSON.stringify(obj.outputData, null, 2);
            });
        });   
    };

    const fetchIdpWorkflowCspDocumentStatus = (companyId, documentGUID, docState) => {

        let idpWorkflowDocumentStatusUrl = `/api/companies/${companyId}/idp/workflows/documents/${documentGUID}?origination=${origination}&documentState=${docState}`;

        axios({
            method: "GET", url: idpWorkflowDocumentStatusUrl,
            headers: { "insightUserId": getUser(), "companyId": getCompanyId(), "Authorization": getToken() }
        })
            .then(response => {
                const JsonData = response.data.data;

                if (JsonData.length) {

                    formatData(JsonData);

                    setIdpWorkflowCspDocumentStatusJsonData(JsonData);
                    //setIdpWorkflowCspDocumentStatusMode('view');
                    setOpenIdpWorkflowCspDocumentStatusDialog(true);
                } else {
                    let message = `No IDP workflow status data exists for CSP document ${documentGUID}`;

                    setNotify({
                        isOpen: true,
                        message: message,
                        severity: 'info'
                    });
                }

            }).catch(error => {
                console.log('error in fetch idp workflow document status api call:', error);

                let message = `Failed to fetch idp workflow document status for workflow [${companyId}, ${documentGUID}]: \n\n ${error.response.statusText} : ${error.response.data.message}`;

                setNotify({
                    isOpen: true,
                    alertTitle: 'Request failed',
                    message: message,
                    severity: 'error'
                });

            });
    };

    const showReprocessInstructions = () => {
        // console.log("reprocess info button clicked");

        setOpenInfoDialog(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleInfoDialogClose = () => {
        setOpenInfoDialog(false);
    };

    const handleIdpWorkflowCspDocumentStatusClose = () => {
        setOpenIdpWorkflowCspDocumentStatusDialog(false);
    };


    return (
        <>
            <div id="processStatusDocumentfilterContainer" style={{ marginTop: '0px', marginLeft: '8px' }}>
                <Filter permission={assetsPermissionOptions} fields={createFilterOptions(fieldData)} sortFields={createSortFieldOptions(sortFieldData)} apiURL={apiURL} fetchData={fetchData} setNotify={setNotify} reload={reload} useCompanyId={true} totalLabel={totalLabel} includeDocActions={hasAssetsUsePermission} tabName={'documentProcessStatusTab'} />
            </div>


            <Divider />

            <div id="processDocumentStatusTableContainer" className={toggleColumnPinning()}>
                <div>
                    <Table data={apiData} columns={getColumns()} hiddenColumns={generateHiddenColumns(columns)} viewRowData={viewRowData} isLoading={isLoading} />
                </div>
            </div>


            {/* This dialog only support view. No need for form or save */}
            <ActionDialog
                open={open}
                mode={mode}
                onClose={handleClose}
                title="Document Information"
                defaultTab='JSON'
                propertiesTabContent={
                    <FormDialog
                        formId={'documentProcessStatusDialogId'}
                        readOnly={true}
                        schema={documentProcessStatusSchemaView}
                        uiSchema={uiSchema}
                        formData={rowJsonData}
                    />
                }
                jsonDataName="documents"
                JSONTabContent={rowJsonData}
            />

            <DocumentIDPWorkflowStatusDialog
                open={openIdpWorkflowCspDocumentStatusDialog}
                onClose={handleIdpWorkflowCspDocumentStatusClose}
                data={idpWorkflowCspDocumentStatusJsonData}
                viewTitle={viewTitle}
                jsonDataName={jsonDataName}
            />

            <DraggableDialog
                open={openInfoDialog}
                onClose={handleInfoDialogClose}
                title={reprocessDialogTitle}
                content={reprocessInstructions}
            />

        </>
    );
}

export default DocumentProcessStatusTabPanel;